import { Download } from 'lucide-react';
import type { TrainingStatus } from '@hcc/types';
import { useTrainingAttemptHistory } from '../../../hooks/useTrainingProgress';
import { ModalShell } from '../../ui/ModalShell';
import { Pill } from '../../ui/Pill';
import { SkeletonList } from '../../ui/Skeleton';
import { formatDateTime } from '../../../lib/dateFmt';
import { StaffCompleteSection } from './StaffCompleteSection';
import { ResetProgressSection } from './ResetProgressSection';

/** The matrix cell click-through: every attempt for one person x module. */
export function AttemptHistoryModal({
  personId,
  taskId,
  moduleTitle,
  graded = true,
  canStaffComplete = false,
  status,
  onClose,
}: {
  personId: string | null;
  taskId: string | null;
  moduleTitle: string;
  /** False = an unmarked assessment: submissions, not scored attempts. */
  graded?: boolean;
  /** External module not yet passed: offer the staff mark-off. */
  canStaffComplete?: boolean;
  /** The cell's status; in-progress and passed cells offer "reset and re-assign". */
  status?: TrainingStatus;
  onClose: () => void;
}) {
  const history = useTrainingAttemptHistory(personId, taskId);
  const open = !!personId && !!taskId;
  return (
    <ModalShell open={open} onClose={onClose} titleId="attempt-history-title">
      <div className="p-4">
        <h2 id="attempt-history-title" className="text-base font-semibold text-fg">
          {history.data?.person.full_name ?? 'Attempts'}
        </h2>
        <p className="mt-0.5 text-sm text-fg-muted">{moduleTitle}</p>
        <div className="mt-3">
          {history.isLoading ? (
            <SkeletonList rows={2} />
          ) : !history.data || history.data.attempts.length === 0 ? (
            <p className="text-sm text-fg-muted">No attempts yet.</p>
          ) : (
            <ol className="divide-y divide-divider/10">
              {history.data.attempts.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="text-fg">
                    {graded ? 'Attempt' : 'Submission'} {a.attempt_number}
                  </span>
                  <span className="text-fg-dim">{formatDateTime(a.completed_at)}</span>
                  <span className="flex items-center gap-2">
                    {graded ? <span className="tabular-nums text-fg-muted">{a.score}%</span> : null}
                    <Pill tone={a.passed ? 'success' : 'warning'} size="sm">
                      {!graded ? 'Recorded' : a.passed ? 'Passed' : 'Failed'}
                    </Pill>
                    {/* The same PDF the module emails on completion. */}
                    <a
                      href={`/api/admin/training-progress/${personId}/${taskId}/attempts/${a.id}/answers.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      title="Download the answers as a PDF"
                      className="inline-flex items-center gap-1 text-accent hover:underline"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Answers
                    </a>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
        {canStaffComplete && personId && taskId ? (
          <StaffCompleteSection personId={personId} taskId={taskId} onDone={onClose} />
        ) : null}
        {personId && taskId && (status === 'in_progress' || status === 'passed') ? (
          <ResetProgressSection
            personId={personId}
            taskId={taskId}
            complete={status === 'passed'}
            onDone={onClose}
          />
        ) : null}
      </div>
    </ModalShell>
  );
}
